import React, { useState, useMemo } from 'react';
import {
    Box,
    TextField,
    Typography,
    IconButton,
    Collapse,
    Divider,
    Button,
    Dialog,
    AppBar,
    Toolbar,
    Slide,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SendIcon from '@mui/icons-material/ArrowUpward';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

// Dialog transition
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const NeighborhoodQueryBox = ({ onLocationExtracted, mapCenter, onCenterLocation }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md')); // full-screen on small/medium screens

    const [query, setQuery] = useState('');
    const [history, setHistory] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const handleAskQuestion = async () => {
        if (!query.trim()) return;
        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ask`, {
                history: [
                    ...history.flatMap(entry => [
                        { role: 'user', content: entry.question },
                        { role: 'assistant', content: JSON.stringify(entry.answer) }
                    ]),
                    { role: 'user', content: query },
                ],
                mapContext: mapCenter,
            });

            const { answer, locations = [] } = response.data;
            if (onLocationExtracted && locations.length > 0) {
                onLocationExtracted(locations);
            }

            setHistory((prev) => [...prev, { question: query, answer: { answer, locations } }]);
            setQuery('');
        } catch (error) {
            console.error('Error fetching AI response:', error);
            setHistory((prev) => [
                ...prev,
                { question: query, answer: { answer: 'Sorry, something went wrong while contacting the AI.', locations: [] } },
            ]);
            setQuery('');
        } finally {
            setLoading(false);
        }
    };

    // When clicking Center Map inside the modal, close & collapse.
    const centerFromModal = (loc) => {
        try {
            onCenterLocation?.(loc);
        } finally {
            setShowModal(false);     // close modal
            setExpanded(false);      // collapse panel until user expands again
        }
    };

    // Reusable content renderer (used in panel and in modal)
    const Conversation = useMemo(() => (
        <Box
            sx={{
                px: 3,
                py: 2,
                maxHeight: '60vh',
                overflowY: 'auto',
                backgroundColor: '#181818',
            }}
        >
            {history.map((entry, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        You:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                        {entry.question}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        Whook:
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#ccc' }}>
                        {entry.answer.answer}
                    </Typography>

                    {entry.answer.locations && entry.answer.locations.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {entry.answer.locations.map((loc, locIdx) => (
                                <Box
                                    key={locIdx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        backgroundColor: '#2a2a2a',
                                        p: 1,
                                        borderRadius: 1
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                            {loc.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#aaa' }}>
                                            {loc.address}
                                        </Typography>
                                    </Box>

                                    {/* If in modal, center + minimize. If in panel, just center. */}
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => (showModal ? centerFromModal(loc) : onCenterLocation?.(loc))}
                                        sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#45a049' } }}
                                    >
                                        Center Map
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {idx < history.length - 1 && <Divider sx={{ my: 2, borderColor: '#2e2e2e' }} />}
                </Box>
            ))}
        </Box>
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [history, showModal]); // re-render buttons depending on modal state

    return (
        <>
            {/* Base Panel */}
            <Box
                sx={{
                    mt: 4,
                    backgroundColor: '#121212',
                    borderRadius: 2,
                    border: '1px solid #2e2e2e',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                    overflow: 'hidden',
                    opacity: showModal ? 0.6 : 1, // slight dim while modal open
                    pointerEvents: showModal ? 'none' : 'auto'
                }}
            >
                <Box
                    onClick={() => setExpanded(prev => !prev)}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 3,
                        py: 2,
                        backgroundColor: '#1e1e1e',
                        borderBottom: '1px solid #2e2e2e',
                        cursor: 'pointer',
                    }}
                >
                    <Typography variant="h6" color="white">
                        Ask About Your Neighborhoods
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Pop-out / Minimize button */}
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation(); // don't toggle collapse
                                setShowModal(true);
                            }}
                            sx={{ color: '#4CAF50' }}
                            aria-label="Open in modal"
                        >
                            <OpenInFullIcon />
                        </IconButton>

                        <IconButton sx={{ color: '#4CAF50' }} aria-label="Toggle">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                <Collapse in={expanded}>
                    {Conversation}

                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            borderTop: '1px solid #2e2e2e',
                            backgroundColor: '#101010',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="e.g. Where are the gyms near Darby Park?"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    input: { color: '#000' },
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAskQuestion();
                                }}
                                inputProps={{ maxLength: 150 }}
                                disabled={loading}
                            />
                            <IconButton
                                onClick={handleAskQuestion}
                                disabled={loading}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#4CAF50',
                                    '&:hover': { backgroundColor: '#222' },
                                    borderRadius: '50%',
                                    width: 48,
                                    height: 48,
                                }}
                                aria-label="Send"
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Collapse>
            </Box>

            {/* Modal */}
            <Dialog
                open={showModal}
                onClose={() => setShowModal(false)}
                TransitionComponent={Transition}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        bgcolor: '#111',
                        borderRadius: fullScreen ? 0 : 2,
                        border: fullScreen ? 'none' : '1px solid #2e2e2e'
                    }
                }}
            >
                <AppBar position="static" sx={{ bgcolor: '#1e1e1e', borderBottom: '1px solid #2e2e2e' }}>
                    <Toolbar sx={{ minHeight: 56 }}>
                        <Typography variant="h6" sx={{ flex: 1, color: '#fff' }}>
                            Ask About Your Neighborhoods
                        </Typography>

                        {/* Minimize button (closes modal) */}
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setShowModal(false)}
                            aria-label="Minimize"
                            sx={{ mr: 1 }}
                            title="Minimize"
                        >
                            <CloseFullscreenIcon />
                        </IconButton>

                        {/* Close button as a fallback */}
                        <IconButton edge="end" color="inherit" onClick={() => setShowModal(false)} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </Toolbar>
                </AppBar>

                {/* Body */}
                <Box sx={{ bgcolor: '#181818' }}>
                    {Conversation}

                    {/* Composer inside modal */}
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            borderTop: '1px solid #2e2e2e',
                            backgroundColor: '#101010',
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="e.g. What times are best for Darby Park?"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    input: { color: '#000' },
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAskQuestion();
                                }}
                                inputProps={{ maxLength: 150 }}
                                disabled={loading}
                            />
                            <IconButton
                                onClick={handleAskQuestion}
                                disabled={loading}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#4CAF50',
                                    '&:hover': { backgroundColor: '#222' },
                                    borderRadius: '50%',
                                    width: 48,
                                    height: 48,
                                }}
                                aria-label="Send"
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default NeighborhoodQueryBox;
